<template>
    <template v-if="mode === 'interno'">
        <div v-if="isVideo">
            <video controls autoplay muted loop style="max-height:500px; width: 100%;">
                <source :src="pregunta.imagen" type="video/mp4" />
                Your browser does not support the video tag.
            </video>
        </div>
        <v-img v-else max-height="500" :src="pregunta.imagen" />
    </template>
    <v-card variant="text">
        <v-row>
            <v-col cols="12" sm="10">
                <v-card-title class="text-wrap" style="word-break: break-word">
                    {{ pregunta.texto }}
                </v-card-title>
            </v-col>
            <v-col cols="auto" class="hidden-xs">
                <v-chip color="success" variant="outlined" class="mt-3">
                    <v-tooltip activator="parent">{{ t('Selecciona_múltiples_respuestas') }}</v-tooltip>
                    <v-icon icon="mdi-checkbox-multiple-marked" start></v-icon>
                    {{ t('múltiple') }}
                </v-chip>
            </v-col>
        </v-row>
        <v-card-text class="pa-0 pa-md-4">
            <v-list v-for="opcion in pregunta.opciones" :key="opcion.id" select-strategy="classic"
                :disabled="haRespondido">
                <v-list-item :value="opcion.id" @click="toggleCheckbox(opcion.id)">
                    <template v-slot:prepend>
                        <v-list-item-action start>
                            <v-checkbox-btn v-model="respuestasSeleccionadas" :value="opcion.id" />
                        </v-list-item-action>
                    </template>
                    <v-list-item-title class="text-wrap" style="word-break: break-word">{{ opcion.texto }}
                        <v-chip v-if="haRespondido"
                            :color="opcion.es_correcta === respuestasSeleccionadas.includes(opcion.id) ? 'green' : 'red'"
                            small>
                            <v-icon>
                                {{ opcion.es_correcta ? 'mdi-check' : 'mdi-close' }}</v-icon>
                        </v-chip>
                    </v-list-item-title>
                    <v-list-item-subtitle v-if="haRespondido && opcion.motivo" class="text-wrap"
                        style="line-clamp: unset !important; -webkit-line-clamp: unset !important; display: block !important; overflow: visible !important; white-space: normal !important; word-break: break-word;">
                        {{ opcion.motivo }}
                    </v-list-item-subtitle>
                </v-list-item>
            </v-list>
        </v-card-text>
    </v-card>
    <v-btn block v-if="!haRespondido && mode !== 'teacher'" :disabled="loading || respuestasSeleccionadas.length === 0"
        color="primary" class="mt-3" @click="comprobar">
        {{ t('Comprueba') }}
    </v-btn>
    <template v-if="haRespondido && mode === 'interno'">
        <div class="text-center">
            <v-chip :color="feedbackColor">
                {{ feedbackText }}
            </v-chip>
            <v-btn block :disabled="loading" color="primary" class="mt-3" @click="next">
                {{ t('Continua') }}
            </v-btn>
        </div>
    </template>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { axios } from '../axios';

const props = defineProps({
    pregunta: {
        type: Object,
        default: () => ({})
    },
    mode: {
        type: String,
        default: 'interno',
        // 'interno'  => El componente hace el axios POST a enviar_respuestas/.
        // 'externo'  => El componente emite el evento con los datos, 
        //               y NO hace la petición dentro.
    },
});

const emits = defineEmits(['respuesta-enviada', 'info-respuesta-multiple']);

const { t } = useI18n();
const respuestasSeleccionadas = ref([]);
const haRespondido = ref(false);
const loading = ref(false);

// Computed para saber si todas las respuestas están bien (ninguna falla)
const isAllCorrect = computed(() => {
    if (!haRespondido.value) return false;
    // IDs de las opciones correctas
    const correctIds = props.pregunta.opciones
        .filter(o => o.es_correcta)
        .map(o => o.id);
    // Chequear que no falte ninguna y que no haya extras
    const missed = correctIds.filter(id => !respuestasSeleccionadas.value.includes(id));
    const extra = respuestasSeleccionadas.value.filter(id => !correctIds.includes(id));
    return missed.length === 0 && extra.length === 0;
});

// Texto de feedback traducido
const feedbackText = computed(() => {
    if (!haRespondido.value) return '';
    return isAllCorrect.value
        ? t('Fantástico')
        : t('Puedes_mejorar');
});

// Color del chip: verde si todo correcto, rojo si hay algún fallo
const feedbackColor = computed(() => {
    if (!haRespondido.value) return undefined;
    return isAllCorrect.value ? 'success' : 'error';
});

function toggleCheckbox(opcionId) {
    const index = respuestasSeleccionadas.value.indexOf(opcionId);
    if (index > -1) {
        respuestasSeleccionadas.value.splice(index, 1);
    } else {
        respuestasSeleccionadas.value.push(opcionId);
    }
}

function comprobar() {
    // enviamos un evento con la info de la respuesta para el "modo externo"
    // (o si el padre quisiera usarlo incluso en modo interno).
    console.log('Selected options before emit:', respuestasSeleccionadas.value);
    emits('info-respuesta-multiple', {
        opcion_respuesta_ids: respuestasSeleccionadas.value,
        pregunta_id: props.pregunta.id,
    });

    if (props.mode === 'interno') {
        // Lógica interna de envío
        enviarCuestionario();
    } else {
        // Modo externo: no hace axios.post. Marcamos "respondido" localmente
        haRespondido.value = true;
    }
}

async function enviarCuestionario() {
    const datosParaEnviar = {
        opcion_respuesta_ids: respuestasSeleccionadas.value
    };
    loading.value = true;
    try {
        const respuesta = await axios.post(`enviar_respuestas/`, datosParaEnviar);
        if (respuesta.data.success) {
            haRespondido.value = true;
        } else {
            console.error('Error al enviar respuestas:', respuesta.data.message);
        }
        loading.value = false;
    } catch (error) {
        console.error('Error al realizar la solicitud:', error);
        loading.value = false;
    }
}

function next() {
    emits('respuesta-enviada', props.pregunta);
    respuestasSeleccionadas.value = [];
}

// Reiniciamos el estado cada vez que la pregunta cambie
watch(
    () => props.pregunta?.id,
    (newId, oldId) => {
        if (newId !== oldId) {
            haRespondido.value = false;
            respuestasSeleccionadas.value = [];
        }
    }
);

// Check if the file is a video (mp4)
const isVideo = computed(() => {
    if (props.pregunta.value && props.pregunta.value.imagen) {
        try {
            const urlObj = new URL(props.pregunta.value.imagen);
            return urlObj.pathname.toLowerCase().endsWith('.mp4');
        } catch (e) {
            // Fallback if URL parsing fails
            return props.pregunta.value.imagen.toLowerCase().includes('.mp4');
        }
    }
    return false;
});

// Inicializar las respuestas seleccionadas con respuestas previas del usuario
onMounted(() => {
    if (props.pregunta.respuestas_usuario && props.pregunta.respuestas_usuario.length > 0) {
        respuestasSeleccionadas.value = props.pregunta.respuestas_usuario.map(
            (respuesta) => respuesta.opcion_respuesta.id
        );
    }
});
</script>